// @ts-check

import React, { useState, useCallback, useEffect } from "react";
import { Asset } from "mibaby-frontend/media/Asset";
import { HtmlToReact } from "./html";
import { StepFormRuled } from "./form-builder";
import fetch from "mibaby-common/util/fetch";
import { useAnonymousInterceptor } from "./with-anonymous";
import { FormPollProvider } from "./poll/form-poll.container";

const statusThemeMap = {
    success: "primary",
    error: "danger",
};

class ErrorWithJson extends Error {
    constructor(jsonData = {}, ...params) {
        super(...params);
        this.jsonData = jsonData;
    }
}

const getTheme = (status, prefix = "") =>
    statusThemeMap[status] ? `${prefix}${statusThemeMap[status]}` : "";

/**
 * @param {string} submitUrl
 * @return {[{
 *  status:"idle"|"submitting"|"success"|"error",
 *  errorMessage?: string,
 *  remainingTries?: number
 *  result: any
 * }, ({formData: any}) => Promise]}
 */
const useApi = (submitUrl, delay = null) => {
    const [state, setState] = useState({
        status: "idle",
        errorMessage: null,
    });

    const submit = useCallback(async ({ formData }) => {
        setState(_ => ({ status: "submitting", errorMessage: null }));

        try {
            const request = await fetch(submitUrl, {
                method: "POST",
                body: JSON.stringify(formData),
            });

            const json = await request.json();

            if (json.error) {
                throw new ErrorWithJson(json, json.error);
            }

            // for drama!
            if (delay) {
                await new Promise(resolve => setTimeout(resolve, delay));
            }

            setState(state => ({ ...state, status: "success", result: json }));
        } catch (e) {
            setState(state => ({
                ...state,
                status: "error",
                errorMessage: e.message,
                remainingTries: e instanceof ErrorWithJson ? e.jsonData.remaining_tries : null,
            }));
        }
    }, []);

    // @ts-ignore
    return [state, submit];
};

const defaultConnectorIcon = <i className="panel-path-connector-icon fa fa-chevron-down" />;
const defaultDangerConnectorIcon = <i className="panel-path-connector-icon fa fa-remove" />;

const PanelPathConnector = ({ state = "default", icon = null }) => (
    <div className={`panel-path-connector panel-path-connector-${state}`}>
        {icon || (state === "danger" ? defaultDangerConnectorIcon : defaultConnectorIcon)}
    </div>
);

export const Header = ({ title, description, asset_versions, asset_mimetype }) => (
    <div className="panel panel-primary contest-header">
        <div className="panel-heading headline">{title}</div>
        {asset_versions && <Asset mimeType={asset_mimetype} versions={asset_versions} />}
        {description && (
            <div className="panel-body">
                <HtmlToReact html={description} preset="basic" />
            </div>
        )}
    </div>
);

export const Instruction = ({ title, description, button, button_text, button_link }) => (
    <div className="panel panel-primary contest-instruction">
        <div className="panel-body">
            <p className="headline">{title}</p>
            {description && (
                <blockquote className="indent-vertical">
                    <HtmlToReact html={description} />
                </blockquote>
            )}
            {button && (
                <div className="text-center">
                    <a href={button_link} className="btn btn-primary">
                        {button_text}
                    </a>
                </div>
            )}
        </div>
    </div>
);

export const Quest = props => (
    <div className={`panel ${getTheme(props.status, "panel-")} contest-quest`}>
        <div className="panel-body">
            <StepFormRuled {...props} />
        </div>
    </div>
);

export const Footer = ({
    submitDisabled = true,
    status = "idle",
    info_text = "",
    error_text = "",
    button_text = "Jetzt am Gewinnspiel teilnehmen!",
    winner_text = "",
    conditions,
    nextDraw = null,
    last,
    onSubmit,
}) => (
    <div className={`panel ${getTheme(status, "panel-")} contest-footer`}>
        <div className="panel-body text-center">
            <div>
                {status === "error" ? (
                    <strong className="text-danger">{error_text}</strong>
                ) : status === "success" ? (
                    <>
                        <strong className="text-primary">Super, das hat geklappt!</strong>
                    </>
                ) : (
                    <p>{info_text}</p>
                )}
            </div>
            <div className="indent-vertical-sm">
                <SubmitButton
                    onSubmit={onSubmit}
                    button_text={button_text}
                    status={status}
                    disabled={submitDisabled}
                />
            </div>
            {status !== "success" && nextDraw && (
                <div>
                    Nächste Auslosung: <strong>{new Date(nextDraw).toLocaleString("de-DE")}</strong>
                </div>
            )}
            {status === "success" && nextDraw && (
                <div className="indent-vertical-sm">
                    <strong>Der Gewinner wird gezogen ...</strong> <br />
                    <strong className="text-xxl text-primary">
                        {new Date(nextDraw).toLocaleString("de-DE")}
                    </strong>
                </div>
            )}
            <div className="text-sm">
                <HtmlToReact html={conditions} preset="basic" />
            </div>
        </div>
        {last && last.electedUser && last.electedUser.firstname && last.contestName && (
            <div className="panel-footer text-center">
                <h4 className="headline text-primary">Gewinn von gestern</h4>
                <p>
                    Es wurde verlost: <strong>{last.contestName}</strong> <br />
                    Gewonnen hat: <strong>{last.electedUser.firstname}</strong>
                </p>
                {winner_text && <HtmlToReact html={winner_text} preset="basic" />}
            </div>
        )}
    </div>
);

const SubmitButton = ({ onSubmit, button_text, status, disabled = false }) => {
    return (
        <div className="contest-submit-button-wrapper">
            {status === "success" ? (
                <div>
                    <p className="headline text-primary">Du bist jetzt beim Gewinnspiel dabei!</p>
                    <div className="success-icon">
                        <i className="mbicon mbicon-check" />
                    </div>
                    <p className="indent-vertical text-xxl journal">Viel Glück! 🍀</p>
                </div>
            ) : (
                <button
                    className={`contest-footer-button btn btn-primary btn-status-${status}`}
                    disabled={disabled || status === "submitting"}
                    onClick={onSubmit}
                >
                    <i
                        className={
                            status === "submitting"
                                ? "fa fa-spinner fa-pulse"
                                : "mbicon mbicon-check"
                        }
                    />
                    {status !== "submitting" && <span>{button_text}</span>}
                </button>
            )}
        </div>
    );
};

const serverExceptionMessageMap = {
    already_submitted: "Du hast bereits am Gewinnspiel teilgenommen!",
    expectation_missmatch: "Ups, Deine Angaben scheinen nicht korrekt zu sein, probiers nochmal!",
    max_tries_exceeded:
        "Tut uns leid, aber Du hast zu oft falsch geantwortet! Du kannst aber gerne morgen wieder am Gewinnspiel teilnehmen!",
};

const getErrorMessage = (errorMessage, fallback = "Ups, da ist etwas schief gegangen.") => {
    return serverExceptionMessageMap[errorMessage] || fallback;
};

const ErrorComponent = ({ errorMessage, remainingTries = null }) => (
    <div className="panel panel-danger">
        <div className="panel-body text-danger headline">{errorMessage}</div>
        {remainingTries !== undefined && remainingTries !== null && (
            <div className="panel-body text-danger text-lg text-center">
                Verbleibende Versuche: {remainingTries}
            </div>
        )}
    </div>
);

export const Contest = ({
    className = "",
    header,
    instruction,
    last,
    config: { registerLightboxId = null } = {},
    quest: { submitUrl, validateUrl, userParticipated, nextDraw, pollStatsUrl, ...quest } = {
        submitUrl: null,
        validateUrl: null,
        pollStatsUrl: null,
        userParticipated: false,
        nextDraw: null,
    },
    footer,
}) => {
    const [formData, updateFormData] = useState(null);

    const [{ status: validationStatus, errorMessage: errorMessage, remainingTries }, onValidate] =
        useApi(validateUrl);

    const [submitting, onSubmit] = useApi(submitUrl);
    const [onValidateAnonymous] = useAnonymousInterceptor(onValidate, null, registerLightboxId);

    // Success page redirect
    const { successPage } = submitting.result || {};
    useEffect(() => {
        if (successPage) {
            document.location = successPage;
        }
    }, [successPage]);

    return (
        <div className={`contest ${className}`}>
            <Header {...header} />
            <PanelPathConnector state="primary" />
            {userParticipated ? (
                <Footer {...footer} status="success" nextDraw={nextDraw} />
            ) : (
                <>
                    <Instruction {...instruction} />
                    <PanelPathConnector state="primary" />
                    <FormPollProvider pollStatsUrl={pollStatsUrl}>
                        <Quest
                            {...quest}
                            onSubmit={onValidateAnonymous}
                            status={validationStatus}
                            onChange={({ formData }) => updateFormData(formData)}
                        />
                    </FormPollProvider>
                    {errorMessage && (
                        <>
                            <PanelPathConnector state="danger" />
                            <ErrorComponent
                                errorMessage={getErrorMessage(errorMessage)}
                                remainingTries={remainingTries}
                            />
                        </>
                    )}
                    <PanelPathConnector state={statusThemeMap[validationStatus] || "default"} />

                    <Footer
                        {...footer}
                        status={submitting.status}
                        submitDisabled={validationStatus !== "success"}
                        onSubmit={() => onSubmit({ formData })}
                        nextDraw={nextDraw}
                        last={last}
                    />
                </>
            )}
        </div>
    );
};
